/**
 * useAnimationPlayer Hook
 * 
 * Manage 3D model animation playback:
 * - Animation mixer setup
 * - Play/pause/restart controls
 * - Speed control (0.1x - 3x)
 * - Progress tracking & seeking
 * - Loop handling
 * 
 * @param {Object} modelRef - Model reference
 * @param {Object} clockRef - Clock reference
 * @returns {Object} Animation player state dan controls
 */

import { useState, useEffect, useRef, useCallback } from 'react'

export function useAnimationPlayer(modelRef, clockRef) {
  const mixerRef = useRef(null)
  const currentActionRef = useRef(null)
  const isDraggingProgressRef = useRef(false)

  const [selectedAnimation, setSelectedAnimation] = useState('')
  const [isPlaying, setIsPlaying] = useState(false)
  const [animationSpeed, setAnimationSpeed] = useState(1)
  const [animationProgress, setAnimationProgress] = useState(0) // 0-100
  const [animationDuration, setAnimationDuration] = useState(0)

  // Use global mixer created by model loader
  useEffect(() => {
    if (!window.ANIMATION_MIXER) return
    
    // Use the mixer created during model load
    mixerRef.current = window.ANIMATION_MIXER
    console.log('✅ Animation mixer connected from model loader')
    
    // Check for animations
    if (modelRef.current?.userData?.animations) {
      const anims = modelRef.current.userData.animations
      console.log(`✅ Mixer ready with ${anims.length} animation(s)`)
    }
  }, [modelRef.current, window.ANIMATION_MIXER])

  // Update mixer in animation loop
  useEffect(() => {
    if (!mixerRef.current || !clockRef.current) {
      console.log('⚠️ Mixer or clock not ready')
      return
    }

    console.log('🔄 Setting up animation update loop...')

    let updateCount = 0
    const updateMixer = () => {
      updateCount++
      if (updateCount === 1 || updateCount % 60 === 0) {
        console.log(`🔄 updateMixer called (count: ${updateCount})`)
      }

      const delta = clockRef.current.getDelta()
      mixerRef.current.update(delta)

      // DEBUG: Check conditions every 60 frames (1 second)
      if (updateCount % 60 === 0) {
        console.log('🔍 Update conditions:', {
          hasAction: !!currentActionRef.current,
          duration: animationDuration,
          isDragging: isDraggingProgressRef.current,
        })
      }

      // Update progress for UI
      if (currentActionRef.current && animationDuration > 0 && !isDraggingProgressRef.current) {
        const currentTime = currentActionRef.current.time
        const progress = (currentTime / animationDuration) * 100
        setAnimationProgress(Math.min(100, progress))

        // DEBUG: Verify inside conditional
        if (updateCount % 60 === 0) {
          console.log('✅ INSIDE conditional! Progress:', progress.toFixed(1))
        }

        // CAMERA INTERPOLATION TRIGGER - Call dari animation loop!
        if (window.CAMERA_INTERPOLATION_CALLBACK && typeof window.CAMERA_INTERPOLATION_CALLBACK === 'function') {
          if (updateCount % 60 === 0) {
            console.log('✅ Calling camera callback...')
          }
          try {
            window.CAMERA_INTERPOLATION_CALLBACK(currentTime, progress)
          } catch (err) {
            console.error('❌ Camera interpolation error:', err)
          }
        } else {
          if (updateCount % 60 === 0) {
            console.log('⚠️ No camera callback!', {
              exists: !!window.CAMERA_INTERPOLATION_CALLBACK,
              type: typeof window.CAMERA_INTERPOLATION_CALLBACK
            })
          }
        }
      }
    }

    // Set interval untuk update (60 FPS)
    const intervalId = setInterval(updateMixer, 1000 / 60)
    console.log('✅ Animation update loop started (60 FPS)')

    return () => {
      clearInterval(intervalId)
      console.log('🧹 Animation update loop stopped')
    }
  }, [animationDuration]) // Re-run saat animationDuration berubah

  // Update mixer on animation change
  useEffect(() => {
    if (
      !mixerRef.current ||
      !modelRef.current?.userData.animations ||
      !selectedAnimation ||
      !window.THREE_MODULE
    ) {
      return
    }

    const THREE = window.THREE_MODULE
    const animations = modelRef.current.userData.animations
    const selectedAnim = animations.find(
      (anim, idx) => (anim.name || `Animation ${idx + 1}`) === selectedAnimation
    )

    if (!selectedAnim) return

    // Set duration
    setAnimationDuration(selectedAnim.duration)

    // Stop all actions and create new one
    mixerRef.current.stopAllAction()
    const action = mixerRef.current.clipAction(selectedAnim)
    action.timeScale = animationSpeed
    action.loop = THREE.LoopRepeat

    // Play to initialize (required for scrubbing)
    action.reset().play()
    action.time = 0
    setAnimationProgress(0)

    // Store action reference
    currentActionRef.current = action
    console.log('✅ Animation action created:', {
      name: selectedAnim.name,
      duration: selectedAnim.duration,
      isPlaying,
      paused: action.paused,
    })

    // Set pause state
    action.paused = !isPlaying
  }, [selectedAnimation, isPlaying])

  // Handle play/pause toggle
  useEffect(() => {
    if (!currentActionRef.current) return

    if (isPlaying) {
      currentActionRef.current.paused = false
      if (!currentActionRef.current.isRunning()) {
        currentActionRef.current.play()
      }
    } else {
      currentActionRef.current.paused = true
    }
  }, [isPlaying])

  // Handle speed change
  useEffect(() => {
    if (!currentActionRef.current) return
    currentActionRef.current.timeScale = animationSpeed
  }, [animationSpeed])

  // Seek animation to specific progress (0-100)
  const seekAnimation = useCallback((progressPercent, skipStateUpdate = false) => {
    if (!currentActionRef.current || !animationDuration) return

    const targetTime = (progressPercent / 100) * animationDuration
    currentActionRef.current.time = targetTime

    if (!skipStateUpdate) {
      setAnimationProgress(progressPercent)
    }
  }, [animationDuration])

  // Restart animation
  const restartAnimation = useCallback(() => {
    if (!currentActionRef.current) return

    currentActionRef.current.reset()
    currentActionRef.current.play()
    currentActionRef.current.paused = false
    setAnimationProgress(0)
    setIsPlaying(true)
  }, [])

  // Cycle speed (0.25x -> 0.5x -> 1x -> 1.5x -> 2x -> back to 0.25x)
  const cycleSpeed = useCallback(() => {
    const speeds = [0.25, 0.5, 1, 1.5, 2]
    const currentIndex = speeds.indexOf(animationSpeed)
    const nextIndex = (currentIndex + 1) % speeds.length
    setAnimationSpeed(speeds[nextIndex])
  }, [animationSpeed])

  return {
    // Refs (for external access)
    mixerRef,
    currentActionRef,
    isDraggingProgressRef,

    // State
    selectedAnimation,
    setSelectedAnimation,
    isPlaying,
    setIsPlaying,
    animationSpeed,
    setAnimationSpeed,
    animationProgress,
    setAnimationProgress,
    animationDuration,

    // Methods
    seekAnimation,
    restartAnimation,
    cycleSpeed,
  }
}

